import React, { useEffect, useState } from 'react';
import { MessageSquareIcon, MicIcon, UsersIcon, BarChart3Icon, CheckCircleIcon, ZapIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Button } from './ui/Button';
const features = [{
  icon: <MessageSquareIcon size={24} className="text-[#7c3aed]" data-id="element-181" />,
  title: 'Live Q&A Collection',
  description: 'Collect and moderate attendee questions in real-time before and during sessions, ensuring the best questions get asked.'
}, {
  icon: <MicIcon size={24} className="text-[#7c3aed]" data-id="element-182" />,
  title: 'Speaker Call Management',
  description: 'Streamline your call for speakers with organized submissions, voting, and transparent review processes.'
}, {
  icon: <UsersIcon size={24} className="text-[#7c3aed]" data-id="element-183" />,
  title: 'Community Input',
  description: 'Let your community vote on topics, suggest sessions, and shape the conference agenda together.'
}, {
  icon: <CheckCircleIcon size={24} className="text-[#7c3aed]" data-id="element-184" />,
  title: 'Transparent Status',
  description: 'Keep speakers and attendees informed with clear submission statuses and real-time updates.'
}, {
  icon: <BarChart3Icon size={24} className="text-[#7c3aed]" data-id="element-185" />,
  title: 'Engagement Analytics',
  description: 'Track participation, identify trending topics, and measure audience engagement throughout your event.'
}, {
  icon: <ZapIcon size={24} className="text-[#7c3aed]" data-id="element-186" />,
  title: 'Fast & Simple',
  description: 'No complex setup required. Launch your submission portal in minutes and focus on creating amazing events.'
}];
const ImageSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = [{
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/egWEEfUi1EGoheCCN9JfUG/Schermafbeelding_2025-08-31_143739.png',
    alt: 'Question submission interface'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bKJuayfKjAwVBrxvwwk17J/Schermafbeelding_2025-08-31_145515.png',
    alt: 'Event analytics dashboard'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/56y6Nh8kztMoHGywtnCp58/Schermafbeelding_2025-08-31_145554.png',
    alt: 'Speaker proposal details'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/idX1XBwkj2chr6BHuHxNtF/Schermafbeelding_2025-08-31_144657.png',
    alt: 'Session categories management'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4yFvW1uRQJ9v17xp6ycdCt/Schermafbeelding_2025-08-31_145212.png',
    alt: 'Team collaboration panel'
  }];
  const nextSlide = () => {
    setCurrentIndex(prevIndex => prevIndex === images.length - 1 ? 0 : prevIndex + 1);
  };
  const prevSlide = () => {
    setCurrentIndex(prevIndex => prevIndex === 0 ? images.length - 1 : prevIndex - 1);
  };
  const goToSlide = index => {
    setCurrentIndex(index);
  };
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  return <div className="relative w-full" data-id="element-187">
      <div className="overflow-hidden rounded-lg shadow-md border-2 border-[#0A4D8C]" data-id="element-188">
        <div className="relative" data-id="element-189">
          <img src={images[currentIndex].src} alt={images[currentIndex].alt} className="w-full rounded-lg mt-1" data-id="element-190" />
          <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white/90 p-2 rounded-full shadow-md transition-colors" aria-label="Previous slide" data-id="element-191">
            <ChevronLeftIcon size={20} className="text-gray-800" data-id="element-192" />
          </button>
          <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white/90 p-2 rounded-full shadow-md transition-colors" aria-label="Next slide" data-id="element-193">
            <ChevronRightIcon size={20} className="text-gray-800" data-id="element-194" />
          </button>
        </div>
      </div>
      <div className="flex justify-center mt-4 space-x-2" data-id="element-195">
        {images.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`w-2.5 h-2.5 rounded-full transition-colors ${index === currentIndex ? 'bg-[#0A4D8C]' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-196" />)}
      </div>
    </div>;
};
export function Features() {
  return <section id="features" className="py-20 bg-white" data-id="element-197">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-198">
        <div className="border-2 border-[#9333EA] rounded-xl p-8 shadow-md" data-id="element-199">
          <div className="text-center mb-16 border-b border-gray-200 pb-12" data-id="element-200">
            <div className="max-w-3xl mx-auto mb-8" data-id="element-201">
              <span className="inline-block px-4 py-1 bg-[#F5E6F5] text-[#9333EA] text-sm font-medium rounded-full mb-4 border border-[#E5B3E5] shadow-sm" data-id="element-202">
                Features
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-203">
                Everything you need for{' '}
                <span className="text-[#9333EA]" data-id="element-204">engaging events</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-100" data-id="element-205">
                From call for speakers to live Q&A, manage every aspect of
                audience participation in one place.
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 mt-8" data-id="element-206">
                <Button size="lg" className="min-w-[180px] shadow-md" href="https://nexioapp.eu/dashboard/register" data-id="element-207">
                  Start your free trial
                </Button>
                <Button variant="outline" size="lg" className="min-w-[180px]" href="https://calendar.app.google/6fKsGXEmBQzjPvnc9" data-id="element-208">
                  Plan a Demo
                </Button>
              </div>
            </div>
            <div className="mt-12 max-w-5xl mx-auto relative" data-id="element-209">
              <div className="absolute -top-6 -left-6 bg-white rounded-lg shadow-lg p-3 w-auto hidden md:block z-10 border border-[#9333EA]" data-id="element-210">
                <div className="flex items-center space-x-2" data-id="element-211">
                  <div className="w-2 h-2 rounded-full bg-[#E5B3E5]" data-id="element-212"></div>
                  <span className="text-sm font-medium text-gray-800" data-id="element-213">
                    Real-time engagement
                  </span>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 bg-white rounded-lg shadow-lg p-3 w-auto hidden md:block z-10 border border-[#9333EA]" data-id="element-214">
                <div className="flex items-center space-x-2" data-id="element-215">
                  <div className="w-2 h-2 rounded-full bg-[#9333EA]" data-id="element-216"></div>
                  <span className="text-sm font-medium text-gray-800" data-id="element-217">
                    Speaker management
                  </span>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-3 w-auto hidden md:block z-10 border border-[#9333EA]" data-id="element-218">
                <div className="flex items-center space-x-2" data-id="element-219">
                  <div className="w-2 h-2 rounded-full bg-[#E5B3E5]" data-id="element-220"></div>
                  <span className="text-sm font-medium text-gray-800" data-id="element-221">
                    Community voting
                  </span>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-lg shadow-lg p-3 w-auto hidden md:block z-10 border border-[#9333EA]" data-id="element-222">
                <div className="flex items-center space-x-2" data-id="element-223">
                  <div className="w-2 h-2 rounded-full bg-[#9333EA]" data-id="element-224"></div>
                  <span className="text-sm font-medium text-gray-800" data-id="element-225">
                    Analytics & insights
                  </span>
                </div>
              </div>
              <ImageSlider data-id="element-226" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12" data-id="element-227">
            {features.map((feature, index) => {
            const bgColors = ['bg-purple-50', 'bg-violet-50', 'bg-pink-50', 'bg-fuchsia-50', 'bg-purple-100', 'bg-pink-100'];
            return <div key={index} className={`p-6 rounded-lg border-2 border-[#9333EA] hover:shadow-md hover:border-[#E5B3E5] hover:bg-[#F5E6F5] transition-all ${bgColors[index % bgColors.length]}`} data-id="element-228">
                  <div className="mb-4 p-2 inline-block bg-[#F5E6F5] rounded-lg border border-[#E5B3E5]" data-id="element-229">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-230">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-231">{feature.description}</p>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}